/**
 * useClienteRepresentantes - Hook for managing cliente representatives
 *
 * Handles: Representantes list, CRUD operations
 */
import { useState, useEffect, useCallback } from 'react';
import { Cliente } from '../services/clienteService';
import { representantesService, type Representante } from '../services/representantesService';
import { msg, MESSAGES } from '../utils/messages';
import { cnpjMatch } from '../utils/cnpjMatch';  // Story 2.55

export interface UseClienteRepresentantesConfig {
  selectedCliente: Cliente | null;
  activeTab: string;
}

export interface UseClienteRepresentantesReturn {
  representantes: Representante[];
  loadingRepresentantes: boolean;
  searchRepresentante: string;
  setSearchRepresentante: (term: string) => void;
  filteredRepresentantes: Representante[];
  loadRepresentantes: () => Promise<void>;
  handleDeleteRepresentante: (id: number, confirmFn: (opts: any) => Promise<boolean>) => Promise<void>;
}

export const useClienteRepresentantes = ({
  selectedCliente,
  activeTab,
}: UseClienteRepresentantesConfig): UseClienteRepresentantesReturn => {
  const [representantes, setRepresentantes] = useState<Representante[]>([]);
  const [loadingRepresentantes, setLoadingRepresentantes] = useState(false);
  const [searchRepresentante, setSearchRepresentante] = useState('');

  // Load representantes
  const loadRepresentantes = useCallback(async () => {
    if (!selectedCliente) return;

    setLoadingRepresentantes(true);
    try {
      const data = await representantesService.getByCliente(selectedCliente.id);
      setRepresentantes(data);
    } catch (err: any) {
      console.error('Erro ao carregar representantes:', err);
      msg.error(MESSAGES.CLIENT.REP_LOAD_ERROR);
    } finally {
      setLoadingRepresentantes(false);
    }
  }, [selectedCliente]);

  // Load when cliente changes (always load to show count in header)
  useEffect(() => {
    if (selectedCliente) {
      loadRepresentantes();
    }
  }, [selectedCliente, loadRepresentantes]);

  // Delete representante
  const handleDeleteRepresentante = useCallback(async (
    id: number,
    confirmFn: (opts: any) => Promise<boolean>
  ) => {
    const confirmed = await confirmFn({
      title: 'Excluir Representante',
      message: MESSAGES.CLIENT.REP_CONFIRM_DELETE,
      variant: 'danger',
      confirmText: 'Excluir',
    });

    if (!confirmed) return;

    try {
      await representantesService.delete(id);
      await loadRepresentantes();
      msg.success(MESSAGES.CLIENT.REP_DELETED);
    } catch (err: any) {
      console.error('Erro ao excluir representante:', err);
      msg.error(MESSAGES.CLIENT.REP_DELETE_ERROR);
    }
  }, [loadRepresentantes]);

  // Filtered representantes
  // Story 2.55: cpf_cnpj normalizado via cnpjMatch (funciona pra CPF 11 ou CNPJ 14 digitos)
  const filteredRepresentantes = representantes.filter(r => {
    const term = searchRepresentante.toLowerCase();
    return (
      r.nome.toLowerCase().includes(term) ||
      cnpjMatch(r.cpf_cnpj, searchRepresentante) ||
      r.email.toLowerCase().includes(term) ||
      r.empresa?.toLowerCase().includes(term)
    );
  });

  return {
    representantes,
    loadingRepresentantes,
    searchRepresentante,
    setSearchRepresentante,
    filteredRepresentantes,
    loadRepresentantes,
    handleDeleteRepresentante,
  };
};

export type { Representante };
